import { Component, OnDestroy, OnInit } from '@angular/core';

interface CookieConsentState {
  necessary: true;
  analytics: boolean;
  version: string;
  timestamp: string;
}

@Component({
  selector: 'app-cookie-consent',
  templateUrl: './cookie-consent.component.html',
  styleUrls: ['./cookie-consent.component.scss']
})
export class CookieConsentComponent implements OnInit, OnDestroy {
  readonly storageKey = 'dotblack_cookie_consent';
  readonly consentVersion = '1.0';

  bannerVisible = false;
  settingsVisible = false;
  analyticsEnabled = false;

  private readonly openSettingsHandler = () => this.openSettings();

  ngOnInit(): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener('dotblack-open-cookie-settings', this.openSettingsHandler);

    const existing = this.readConsent();
    if (!existing || existing.version !== this.consentVersion) {
      this.bannerVisible = true;
      return;
    }

    this.analyticsEnabled = !!existing.analytics;
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('dotblack-open-cookie-settings', this.openSettingsHandler);
    }
  }

  openSettings(): void {
    const existing = this.readConsent();
    this.analyticsEnabled = !!existing?.analytics;
    this.bannerVisible = false;
    this.settingsVisible = true;
  }

  closeSettings(): void {
    this.settingsVisible = false;
    if (!this.readConsent()) {
      this.bannerVisible = true;
    }
  }

  dismissBanner(): void {
    // Closing the banner is not consent. No preference is stored.
    this.bannerVisible = false;
  }

  rejectNonEssential(): void {
    this.analyticsEnabled = false;
    this.persistConsent(false);
  }

  allowAll(): void {
    this.analyticsEnabled = true;
    this.persistConsent(true);
  }

  savePreferences(): void {
    this.persistConsent(this.analyticsEnabled);
  }

  private persistConsent(analytics: boolean): void {
    if (typeof window === 'undefined') {
      return;
    }

    const consent: CookieConsentState = {
      necessary: true,
      analytics,
      version: this.consentVersion,
      timestamp: new Date().toISOString()
    };

    localStorage.setItem(this.storageKey, JSON.stringify(consent));
    this.bannerVisible = false;
    this.settingsVisible = false;

    window.dispatchEvent(new CustomEvent('dotblack-cookie-consent-changed', {
      detail: consent
    }));
  }

  private readConsent(): CookieConsentState | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) as CookieConsentState : null;
    } catch {
      return null;
    }
  }
}
